import { httpClient } from "infra/axios/axios-api";
import { translateText } from "infra/translation/translate";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import { MandatorySignal } from "presentation/components/Shared/MandatorySignalComponent";
import Spinner from "presentation/components/Shared/Spinner";
import { useEmployeeFieldsStore } from "presentation/stores/use-employee-fields-store";
import { useEmployeeStore } from "presentation/stores/use-employee-store";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { Avatar } from "primereact/avatar";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import DependentsModal from "../DependentsModal";
import { useDependentsByFilter } from "presentation/hooks/use-query-dependent";
import { Tooltip } from "primereact/tooltip";

interface PersonalInfoFormProps {
  canEdit: boolean;
};

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({ canEdit }) => {

  const allowedContractTypesToHaveDependents = ["CTI", "CTP"]

  const { employee, setEmployee } = useEmployeeStore();
  const { employeeFields } = useEmployeeFieldsStore();
  const { maritalStatus, idDocumentType, employeeCountry } =
    useParameterStore();
  const [image, setImage] = useState<string | null>(null);
  const fileUploadRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const toast = useRef<Toast>(null);
  const [showDependentsModal, setShowDependentsModal] = useState(false);

  const { data: dependentsData, refetch: refetchDependents } = useDependentsByFilter({
    employeeId: employee?.id || "",
  });

  const dependentsCount = dependentsData?.data?.length || 0;

  useEffect(() => {
    const fetchImageUrl = async () => {
      if (employee?.photo) {
        try {
          const response = await httpClient.get(
            `/assets/${employee.photo}`
          );
          setImage(response.data.url);
        } catch (error) {
          console.error("Erro ao buscar imagem do funcionário:", error);
        }
      }
      setLoading(false);
    };

    fetchImageUrl();
  }, [employee?.photo]);

  const handleUpload = async (event: FileUploadHandlerEvent) => {
    if (!employee?.id) {
      console.error("ID do funcionário ausente.");
      return;
    }
    const file = event.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const folder = `employees/${employee.id}`;

      const response = await httpClient.post(
        `/assets/${folder}`,
        formData
      );

      const s3Path: string = response.data.filePath;
      // setImage(`${s3Path}`);
      setEmployee({ ...employee, photo: s3Path });
    } catch (error) {
      console.error("Erro no upload:", error);
    } finally {
      event.options.clear();
    }
  };

  const handleValidationFail = (file: File) => {
    toast.current?.show({
      severity: "error",
      summary: "Erro",
      detail: `O arquivo "${file.name}" excede o tamanho máximo permitido (10MB).`,
      life: 4000,
    });
  };

  const getInvalid = (field: string) =>
    employeeFields.find((item) => item.field === field)?.invalid;

  if (loading) return <Spinner />;

  return (
    <>
      <Tooltip
        target=".dependents-tooltip"
        position="top"
      />

      <div className="employeeContainer">
        <div className="employee-photo">
          <div className="border-1 p-3 photo-border-color">
            <Toast ref={toast} />
            <Avatar image={image || ""} className={"avatar"} icon="pi pi-user" />
            {canEdit &&
              <FileUpload
                ref={fileUploadRef}
                name="demo[]"
                mode="basic"
                accept="image/*"
                maxFileSize={10000000}
                chooseLabel={
                  image ? translateText("Edit", "pt") : translateText("Save", "pt")
                }
                customUpload
                auto
                uploadHandler={handleUpload}
                onValidationFail={handleValidationFail}
                className="p-mt-2 w-100"
              />
            }

          </div>
        </div>
        <div className="row-one">
          <div className="default-2-cols">
            <InputTextComponent
              label={`${translateText("Full name", "pt")} ${MandatorySignal()}`}
              fieldName="name"
              currentObject={employee}
              getValue={employee?.name}
              setValue={setEmployee}
              isInvalid={getInvalid("name")}
              isDisable={!canEdit}
            />
          </div>
          <div className="default-1-col">
            <InputDateComponent
              label={`${translateText("Birth date", "pt")} ${MandatorySignal()}`}
              fieldName="birthDate"
              currentObject={employee}
              getValue={employee?.birthDate}
              setValue={setEmployee}
              isInvalid={getInvalid("birthDate")}
              maxDate={new Date("2011-01-01")}
              isDisable={!canEdit}
            />
          </div>
          <div className="default-1-col">
            <InputTextComponent
              label={`${translateText(
                "Personal phone",
                "pt"
              )} ${MandatorySignal()}`}
              fieldName="phoneNumber"
              currentObject={employee}
              getValue={employee?.phoneNumber}
              setValue={setEmployee}
              isInvalid={getInvalid("phoneNumber")}
              isDisable={!canEdit}
            />
          </div>
          <div className="default-2-cols">
            <InputTextComponent
              label={`${translateText(
                "Personal email",
                "pt"
              )} ${MandatorySignal()}`}
              fieldName="personalEmail"
              currentObject={employee}
              getValue={employee?.personalEmail}
              setValue={setEmployee}
              isInvalid={getInvalid("personalEmail")}
              isDisable={!canEdit}
            />
          </div>
          <div className="default-1-col">
            <DropdownComponent
              options={employeeCountry}
              label={`${translateText("Country", "pt")} ${MandatorySignal()}`}
              fieldName="country"
              currentObject={employee}
              getValue={employee?.country}
              setValue={setEmployee}
              isInvalid={getInvalid("country")}
              isDisable={!canEdit}
            />
          </div>
          <div className="default-1-col">
            <InputTextComponent
              label={`${translateText("Postal code", "pt")} ${MandatorySignal()}`}
              fieldName="postalCode"
              currentObject={employee}
              getValue={employee?.postalCode}
              setValue={setEmployee}
              isInvalid={getInvalid("postalCode")}
              isDisable={!canEdit}
            />
          </div>
          <div className="default-1-col">
            <InputTextComponent
              label={`${translateText("City", "pt")} ${MandatorySignal()}`}
              fieldName="city"
              currentObject={employee}
              getValue={employee?.city}
              setValue={setEmployee}
              isInvalid={getInvalid("city")}
              isDisable={!canEdit}
            />
          </div>
        </div>
        <div className="container-row-two">
          <div className="row-two">
            {/* Line 3 */}
            <div className="default-2-cols">
              <InputTextComponent
                label={`${translateText("Address", "pt")} ${MandatorySignal()}`}
                fieldName="address"
                currentObject={employee}
                getValue={employee?.address}
                setValue={setEmployee}
                isInvalid={getInvalid("address")}
                isDisable={!canEdit}
              />
            </div>

            <div className="default-1-col">
              <InputTextComponent
                label={translateText("Address complement", "pt")}
                fieldName="complement"
                currentObject={employee}
                getValue={employee?.complement}
                setValue={setEmployee}
                isInvalid={getInvalid("complement")}
                isDisable={!canEdit}
              />
            </div>

            {/* Line 4 */}

            <div className="default-1-col">
              <DropdownComponent
                options={idDocumentType}
                label={`${translateText(
                  "Id document type",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="idDocumentType"
                currentObject={employee}
                getValue={employee?.idDocumentType}
                setValue={setEmployee}
                isInvalid={getInvalid("idDocumentType")}
                isDisable={!canEdit}
              />
            </div>

            <div className="default-1-col">
              <InputTextComponent
                label={`${translateText(
                  "Id document number",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="idDocumentNumber"
                currentObject={employee}
                getValue={employee?.idDocumentNumber}
                setValue={setEmployee}
                isInvalid={getInvalid("idDocumentNumber")}
                isDisable={!canEdit}
              />
            </div>

            {/* Line 5 */}
            <div className="default-1-col">
              <InputTextComponent
                label={`${translateText(
                  "Tax number",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="fiscalIdNumber"
                currentObject={employee}
                getValue={employee?.fiscalIdNumber}
                setValue={setEmployee}
                isInvalid={getInvalid("fiscalIdNumber")}
                isDisable={!canEdit}
              />
            </div>

            <div className="default-1-col">
              <InputTextComponent
                label={`${translateText(
                  "Social security number",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="socialSecurityNumber"
                currentObject={employee}
                getValue={employee?.socialSecurityNumber}
                setValue={setEmployee}
                isInvalid={getInvalid("socialSecurityNumber")}
                isDisable={!canEdit}
              />
            </div>

            <div className="default-1-col">
              <DropdownComponent
                options={[
                  { id: "Sim", value: "Sim", type: "drivingLicence" },
                  { id: "Não", value: "Não", type: "drivingLicence" },
                ]}
                label={translateText("Driving licence", "pt")}
                fieldName="hasDrivingLicence"
                currentObject={employee}
                getValue={employee?.hasDrivingLicence ? "Sim" : "Não"}
                setValue={(obj: any) => {
                  const value = typeof obj === 'object' ? obj.hasDrivingLicence : obj;
                  setEmployee({
                    hasDrivingLicence: value === "Sim"
                  });
                }}
                isInvalid={getInvalid("hasDrivingLicence")}
                isDisable={!canEdit}
                showClear={true}
              />
            </div>
            <div className="default-1-col">
              <InputTextComponent
                label={`${translateText(
                  "Bank account",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="bankAccount"
                currentObject={employee}
                getValue={employee?.bankAccount}
                setValue={setEmployee}
                isInvalid={getInvalid("bankAccount")}
                isDisable={!canEdit}
              />
            </div>

            {/* Line 6 */}
            <div className="default-1-col">
              <DropdownComponent
                options={maritalStatus}
                label={`${translateText(
                  "Marital status",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="maritalStatus"
                currentObject={employee}
                getValue={employee?.maritalStatus}
                setValue={setEmployee}
                isInvalid={getInvalid("maritalStatus")}
                isDisable={!canEdit}
              />
            </div>
            <div className="default-1-col">
              <FloatLabel>
                <div className="p-inputgroup">
                  <InputText
                    id="dependentsCount"
                    value={dependentsCount.toString()}
                    disabled
                    className="w-full"
                    style={{ textAlign: "center" }}
                  />
                  <span
                    className="dependents-tooltip"
                    data-pr-tooltip={
                      !employee?.id
                        ? translateText("Save employee first to add dependents", "pt")
                        : !allowedContractTypesToHaveDependents.includes(employee.contractType)
                          ? "Apenas colaboradores com contrato CTI ou CTP podem ter dependentes associados."
                          : translateText("Manage dependents", "pt")
                    }
                    style={{ display: "inline-flex" }}
                  >
                    <Button
                      icon="pi pi-users"
                      type="button"
                      onClick={() => setShowDependentsModal(true)}
                      disabled={!employee?.id || !allowedContractTypesToHaveDependents.includes(employee.contractType)}
                    // tooltip={
                    //   !employee?.id
                    //     ? translateText("Save employee first to add dependents", "pt")
                    //     : !allowedContractTypesToHaveDependents.includes(employee.contractType) 
                    //     ? "Apenas colaboradores com contrato CTI ou CTP podem ter dependentes associados."
                    //     : translateText("Manage dependents", "pt")
                    // }
                    // tooltipOptions={{ position: "top" }}
                    />
                  </span>
                </div>
                <label htmlFor="dependentsCount">
                  {`${translateText("Number of dependents", "pt")} ${MandatorySignal()}`}
                </label>
              </FloatLabel>
            </div>

            <div className="default-1-col">
              <InputTextComponent
                label={`${translateText(
                  "Emergency contact name",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="emergencyContactName"
                currentObject={employee}
                getValue={employee?.emergencyContactName}
                setValue={setEmployee}
                isInvalid={getInvalid("emergencyContactName")}
                isDisable={!canEdit}
              />
            </div>
            <div className="default-1-col">
              <InputTextComponent
                label={`${translateText(
                  "Relation to emergency contact",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="emergencyContactRelation"
                currentObject={employee}
                getValue={employee?.emergencyContactRelation}
                setValue={setEmployee}
                isInvalid={getInvalid("emergencyContactRelation")}
                isDisable={!canEdit}
              />
            </div>

            <div className="default-1-col">
              <InputTextComponent
                label={`${translateText(
                  "Emergency contact",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="emergencyContactPhone"
                currentObject={employee}
                getValue={employee?.emergencyContactPhone}
                setValue={setEmployee}
                isInvalid={getInvalid("emergencyContactPhone")}
                isDisable={!canEdit}
              />
            </div>
          </div>
        </div>
        {employee?.id && (
          <DependentsModal
            visible={showDependentsModal}
            onHide={() => {
              setShowDependentsModal(false);
              refetchDependents();
            }}
            employeeId={employee.id}
            canEdit={canEdit}
          />
        )}
      </div>
    </>
  );
};

export default PersonalInfoForm;
